import React, { useState, useEffect } from "react";
import { ChevronLeft, ChevronRight } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";

interface ImagePreviewModalProps {
  images: string[];
  initialIndex?: number;
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  title?: string;
  description?: string;
}

const ImagePreviewModal: React.FC<ImagePreviewModalProps> = ({
  images,
  initialIndex = 0,
  isOpen,
  onOpenChange,
  title = "Review Images",
  description
}) => {
  const [currentIndex, setCurrentIndex] = useState(initialIndex);
  const [isLoading, setIsLoading] = useState(true);
  const [hasError, setHasError] = useState(false);

  // Reset to initial index when modal opens
  useEffect(() => {
    if (isOpen) {
      setCurrentIndex(initialIndex);
      setIsLoading(true);
      setHasError(false);
    }
  }, [isOpen, initialIndex]);

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : images.length - 1));
    setIsLoading(true);
    setHasError(false);
  };

  const handleNext = () => {
    setCurrentIndex((prev) => (prev < images.length - 1 ? prev + 1 : 0));
    setIsLoading(true);
    setHasError(false);
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "ArrowLeft") {
      handlePrevious();
    } else if (e.key === "ArrowRight") {
      handleNext();
    } else if (e.key === "Escape") {
      onOpenChange(false);
    }
  };


  if (!images || images.length === 0) {
    return null;
  }

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent 
        className="max-w-4xl max-h-[90vh] p-0 overflow-hidden bg-white"
        onKeyDown={handleKeyDown}
      >
        {/* Header */}
        <DialogHeader className="p-6 pb-4 border-b border-gray-200">
          <div className="flex items-center justify-between">
            <div>
              <DialogTitle className="text-xl font-semibold text-gray-900">
                {title}
              </DialogTitle>
              {description && (
                <p className="text-sm text-gray-600 mt-1">{description}</p>
              )}
            </div>
            <Badge variant="secondary" className="bg-jamaica-green/10 text-jamaica-green">
              {currentIndex + 1} of {images.length}
            </Badge>
          </div>
        </DialogHeader>

        {/* Image Display */}
        <div className="relative flex-1 bg-gray-50 flex items-center justify-center min-h-[400px]">
          {/* Navigation Buttons */}
          {images.length > 1 && (
            <>
              <Button
                variant="outline"
                size="sm"
                onClick={handlePrevious}
                className="absolute left-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white shadow-lg border-gray-200 z-10"
              >
                <ChevronLeft className="h-4 w-4" />
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={handleNext}
                className="absolute right-4 top-1/2 transform -translate-y-1/2 bg-white/90 hover:bg-white shadow-lg border-gray-200 z-10"
              >
                <ChevronRight className="h-4 w-4" />
              </Button>
            </>
          )}

          {/* Loading Spinner - Absolutely positioned for perfect centering */}
          {isLoading && (
            <div className="absolute inset-0 flex items-center justify-center z-20">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-jamaica-green"></div>
            </div>
          )}

          {/* Error State - Absolutely positioned for perfect centering */}
          {hasError && (
            <div className="absolute inset-0 flex items-center justify-center z-20">
              <div className="text-center text-gray-500 bg-white/90 backdrop-blur-sm rounded-lg p-6 shadow-lg">
                <p className="text-sm">Failed to load image</p>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => {
                    setIsLoading(true);
                    setHasError(false);
                  }}
                  className="mt-2"
                >
                  Retry
                </Button>
              </div>
            </div>
          )}

          {/* Main Image */}
          <div className="w-full h-full flex items-center justify-center p-6">
            <img
              src={images[currentIndex]}
              alt={`Review image ${currentIndex + 1}`}
              className={`max-w-full max-h-full object-contain rounded-lg shadow-lg transition-opacity duration-300 ${
                isLoading || hasError ? 'opacity-0' : 'opacity-100'
              }`}
              onLoad={() => setIsLoading(false)}
              onError={() => {
                setIsLoading(false);
                setHasError(true);
              }}
            />
          </div>
        </div>

        {/* Footer with Thumbnail Navigation */}
        {images.length > 1 && (
          <div className="p-6 pt-4 border-t border-gray-200 bg-white">
            <div className="flex items-center justify-center">
              {/* Thumbnail Navigation */}
              <div className="flex items-center gap-2 overflow-x-auto">
                {images.map((image, index) => (
                  <button
                    key={index}
                    onClick={() => {
                      setCurrentIndex(index);
                      setIsLoading(true);
                      setHasError(false);
                    }}
                    className={`flex-shrink-0 w-12 h-12 rounded-lg overflow-hidden border-2 transition-all ${
                      index === currentIndex
                        ? 'border-jamaica-green shadow-md'
                        : 'border-gray-200 hover:border-jamaica-green/50'
                    }`}
                  >
                    <img
                      src={image}
                      alt={`Thumbnail ${index + 1}`}
                      className="w-full h-full object-cover"
                    />
                  </button>
                ))}
              </div>
            </div>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default ImagePreviewModal;